import React from 'react';
import useTranslation from 'next-translate/useTranslation';
import type { UseFormReturnType } from '@mantine/form';
import { IconUser, IconLock } from '@tabler/icons-react';

import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import dt from '~/testing';

import { ConnectedRegistrationBonusBanner } from '../../RegistrationBonusBanner/ConnectedRegistrationBonusBanner';
import type { KYCFormValues } from '../types';

import { SignUpContinueButton } from './SignUpContinueButton';
import useStyles from './styles';

const dtIds = dt.components.shared.auth.signUpVersionTwo.confirmInfoCard;

interface ConfirmInfoCardProps {
  form: UseFormReturnType<KYCFormValues>;
  onConfirm: () => void;
  onEdit: () => void;
}

export function ConfirmInfoCard({ form, onConfirm, onEdit }: ConfirmInfoCardProps) {
  const { classes, theme } = useStyles();
  const { t } = useTranslation('auth');

  const { firstName, lastName, dob, address, city, state, zip } = form.values;

  return (
    <div className={classes.formContainer}>
      <Title variant="subhead-x-large" weight={700} c="gray2.7">
        {t('signUp.headers.confirmInfo.title')}
      </Title>

      <div className={classes.confirmInfoCardOuter}>
        <div className={classes.confirmInfoCardInner}>
          <button
            type="button"
            className={classes.confirmInfoEditButton}
            onClick={onEdit}
            data-test-id={dtIds.editButton}
          >
            {t('signUp.headers.confirmInfo.edit')}
          </button>

          <div className={classes.confirmInfoAvatar}>
            <IconUser size={28} color={theme.colors.gray[6]} />
          </div>

          <div className={classes.confirmInfoDetails}>
            <Title variant="subhead-x-large" weight={700} c="gray2.7" align="center">
              {firstName} {lastName}
            </Title>
            <Text variant="title-large" c="gray2.4" align="center">
              {t('signUp.confirmInfoCard.born')} {dob.month}/{dob.day}/{dob.year}
            </Text>
            <Text variant="title-large" c="gray2.7" align="center">
              {address}
              {'\n'}
              {city}, {state} {zip}
            </Text>
          </div>
        </div>
      </div>

      <ConnectedRegistrationBonusBanner />

      <SignUpContinueButton
        onClick={onConfirm}
        buttonText={t('signUpActions.verifyAndContinue')}
        data-test-id={dtIds.continueButton}
      />

      <Text variant="body-small" c="gray2.3">
        {t('signUp.confirmInfoCard.prefillDisclaimer')}
      </Text>

      <div className={classes.confirmInfoSecurityNote}>
        <IconLock size={24} color={theme.colors.gray[5]} />
        <Text variant="body-small" c="gray2.4">
          {t('signUp.confirmInfoCard.securityNote')}{' '}
          <Text
            variant="body-small"
            c="blue.3"
            fw={700}
            component="a"
            href="mailto:support@underdogfantasy.com"
            style={{ textDecoration: 'none' }}
          >
            {t('signUp.confirmInfoCard.contactSupport')}
          </Text>
        </Text>
      </div>
    </div>
  );
}
